// Live room gallery. Every device receives everybody's small preview cut-outs
// right after each shot and composes the (watermarked) photos locally, so the
// whole room sees the pictures instantly — nothing has to be "sent back".
// Once the host has stored the session on the server, the items get photo ids
// and become downloadable in HD / Ultra.

import type { PhotoRef } from '../api/client';
import type { SessionInfo } from '../net/protocol';
import type { FaceBox, PoseAdjust } from './layout';
import type { PoseFrame } from './pose';
import type { Decision } from './solver';
import { composeShot, composeStrip, type PersonShot, type StripOptions } from './strip';
import { layoutById } from './themes';

export interface GalleryItem {
  key: string;
  sessionId: string;
  kind: 'shot' | 'strip';
  index: number;
  /** object URL of the locally composed, watermarked preview */
  previewUrl: string | null;
  /** set once the server stored it */
  photo: PhotoRef | null;
  /** missing people when the preview was composed (timeout) */
  partial: boolean;
  /** v0.9.7: previewUrl is the server's finished photo (the phone's provisional one never replaces it) */
  server?: boolean;
}

interface SessionState {
  info: SessionInfo;
  previews: Map<number, Map<number, PersonShot>>;
  timers: Map<number, ReturnType<typeof setTimeout>>;
}

const toBlob = (c: HTMLCanvasElement, q = 0.85) =>
  new Promise<Blob>((res) => c.toBlob((b) => res(b!), 'image/jpeg', q));

export class RoomGallery {
  items: GalleryItem[] = [];
  onChange: (() => void) | null = null;
  /** fired when the strip preview of a session is ready */
  onStrip: ((item: GalleryItem) => void) | null = null;
  private sessions = new Map<string, SessionState>();

  constructor(private customBackground: () => CanvasImageSource & { width: number; height: number } | null) {}

  startSession(info: SessionInfo) {
    if (this.sessions.has(info.session)) return;
    this.sessions.set(info.session, { info, previews: new Map(), timers: new Map() });
  }

  hasSession(id: string) {
    return this.sessions.has(id);
  }

  addPreview(sessionId: string, index: number, slot: number, image: ImageBitmap, face: FaceBox, adjust: PoseAdjust,
    extra: { mirrored: boolean; noFace: boolean; id?: string; pose?: PoseFrame | null; decision?: Decision | null } = { mirrored: true, noFace: false }) {
    const s = this.sessions.get(sessionId);
    if (!s) return;
    let m = s.previews.get(index);
    if (!m) s.previews.set(index, (m = new Map()));
    m.set(slot, { image, face, adjust, slot, ...extra });
    const complete = s.info.slots.every((sl) => m!.has(sl));
    clearTimeout(s.timers.get(index));
    if (complete) void this.composeShotItem(s, index, false);
    // a peer may drop out mid-session: show what we have after a short wait
    else s.timers.set(index, setTimeout(() => void this.composeShotItem(s, index, true), 4000));
  }

  /** v0.9: replaces the phone's provisional picture with the server's final one. */
  useServerImage(sessionId: string, kind: 'strip' | 'shot', index: number, url: string) {
    const key = `${sessionId}:${kind}:${index}`;
    let it = this.items.find((i) => i.key === key);
    if (!it) {
      it = { key, sessionId, kind, index, previewUrl: null, photo: null, partial: false };
      this.items.push(it);
      this.sort();
    }
    if (it.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(it.previewUrl);
    it.previewUrl = url;
    it.partial = false;
    it.server = true;
    this.onChange?.();
  }

  setPhotos(photos: PhotoRef[]) {
    for (const p of photos) {
      const key = `${p.sessionId}:${p.kind}:${p.index}`;
      const it = this.items.find((i) => i.key === key);
      if (it) it.photo = p;
      else this.items.push({ key, sessionId: p.sessionId, kind: p.kind, index: p.index, previewUrl: null, photo: p, partial: false });
    }
    this.sort();
    this.onChange?.();
  }

  private options(info: SessionInfo): StripOptions {
    const c = info.config;
    return {
      layout: c.layout, frameColor: c.frameColor, background: c.background, sceneSlot: c.sceneSlot,
      customBackground: this.customBackground(), filter: c.filter, caption: c.caption,
      date: new Date(info.startedAt), stickers: c.stickers,
    };
  }

  private upsert(item: Omit<GalleryItem, 'photo'>) {
    const old = this.items.find((i) => i.key === item.key);
    if (old) {
      // the server's finished photo is already there: the phone's provisional one is not needed
      if (old.server) { if (item.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(item.previewUrl); return old; }
      if (old.previewUrl) URL.revokeObjectURL(old.previewUrl);
      Object.assign(old, item);
      return old;
    }
    const it = { ...item, photo: null };
    this.items.push(it);
    this.sort();
    return it;
  }

  private sort() {
    this.items.sort((a, b) =>
      a.sessionId === b.sessionId
        ? (a.kind === b.kind ? a.index - b.index : a.kind === 'strip' ? -1 : 1)
        : (this.sessions.get(b.sessionId)?.info.startedAt ?? 0) - (this.sessions.get(a.sessionId)?.info.startedAt ?? 0));
  }

  private async composeShotItem(s: SessionState, index: number, partial: boolean) {
    clearTimeout(s.timers.get(index));
    const people = [...(s.previews.get(index)?.values() ?? [])];
    const canvas = composeShot(people, this.options(s.info), { scale: 0.5, watermark: true });
    const url = URL.createObjectURL(await toBlob(canvas));
    this.upsert({ key: `${s.info.session}:shot:${index}`, sessionId: s.info.session, kind: 'shot', index, previewUrl: url, partial });
    this.onChange?.();
    const cells = layoutById(s.info.config.layout).cells.length;
    const done = Array.from({ length: Math.min(cells, s.info.total) }, (_, i) =>
      this.items.some((it) => it.key === `${s.info.session}:shot:${i}`)).every(Boolean);
    if (done) await this.composeStripItem(s);
  }

  private async composeStripItem(s: SessionState) {
    const total = layoutById(s.info.config.layout).cells.length;
    const shots = Array.from({ length: total }, (_, i) => [...(s.previews.get(i)?.values() ?? [])]);
    const canvas = composeStrip(shots, this.options(s.info), { scale: 1, watermark: true });
    const url = URL.createObjectURL(await toBlob(canvas, 0.82));
    const partial = shots.some((p) => p.length < s.info.slots.length);
    const item = this.upsert({ key: `${s.info.session}:strip:0`, sessionId: s.info.session, kind: 'strip', index: 0, previewUrl: url, partial });
    this.onChange?.();
    this.onStrip?.(item);
  }
}
